import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, 
  Minus, 
  Send, 
  AlertCircle, 
  X,
  UserPlus,
  UserMinus,
  UserCheck,
  Search,
  LogOut,
  Menu,
  Clock
} from 'lucide-react';
import UserSearch from './UserSearch';
import { User as UserType } from '../types/User';
import { approvalAPI } from '../services/ApprovalAPI';

const RemoveRolePage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedUser, setSelectedUser] = useState<UserType | null>(null);
  const [selectedToRemove, setSelectedToRemove] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const tabs = [
    { id: 'add', label: 'Add Role', icon: UserPlus, color: 'text-green-600' },
    { id: 'remove', label: 'Remove Role', icon: UserMinus, color: 'text-red-600' },
    { id: 'change', label: 'Change Role', icon: UserCheck, color: 'text-blue-600' },
    { id: 'requests', label: 'Open Requests', icon: Clock, color: 'text-orange-600' },
  ];

  const handleTabClick = (tabId: string) => {
    if (tabId === 'remove') {
      navigate('/roles/remove');
    } else {
      navigate('/dashboard');
    }
  };

  const handleFetchCurrentRoles = async () => {
    if (!selectedUser?.sapId) return;
    try {
      setIsLoading(true);
      const rolesResult = await approvalAPI.getCurrentSapRoles(selectedUser.sapId);

      let currentRoles: string[] = [];
      let rolesData: any = null;
      if (Array.isArray(rolesResult)) {
        rolesData = rolesResult;
      } else if ((rolesResult as any).Roles) {
        rolesData = (rolesResult as any).Roles;
      }

      if (Array.isArray(rolesData)) {
        currentRoles = rolesData
          .map((role: any) => (typeof role === 'string' ? role : (role.agrName || role.RoleName || role.roleName || '')))
          .filter((name: string) => !!name);
      } else if (rolesData && typeof rolesData === 'object') {
        currentRoles = Object.values(rolesData).filter((v): v is string => typeof v === 'string');
      }

      setSelectedUser({ ...selectedUser, currentRoles });
    } catch (e) {
      setMessage({ type: 'error', text: 'Failed to fetch current roles. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const submitRemoveRoles = async () => {
    if (!selectedUser || selectedToRemove.length === 0) return;
    setIsLoading(true);
    setMessage(null);
    try {
      const result = await approvalAPI.sendApprovalRequest({
        requestType: 'RemoveRole',
        userId: selectedUser.id,
        userDisplayName: selectedUser.name,
        sapUsername: selectedUser.sapId,
        roleId: selectedToRemove[0],
        roleName: selectedToRemove[0],
        requestedBy: 'Current User',
        justification: `Request removal of role ${selectedToRemove[0]}`,
        managerEmail: selectedUser.manager.email,
        managerName: selectedUser.manager.name,
      } as any);

      if (result.success) {
        setMessage({ type: 'success', text: `Request to remove ${selectedToRemove.length} role(s) sent to ${selectedUser.manager.name}.` });
        setSelectedToRemove([]);
      } else {
        setMessage({ type: 'error', text: `Failed to send removal request: ${result.message}` });
      }
    } catch (e) {
      setMessage({ type: 'error', text: 'Failed to send removal request. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button
                className="md:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
                onClick={() => setSidebarOpen(!sidebarOpen)}
              >
                {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
              <div className="flex items-center ml-4 md:ml-0">
                <Shield className="h-8 w-8 text-primary-600" />
                <h1 className="ml-2 text-xl font-bold text-gray-900">NYX+</h1>
                <span className="ml-2 text-sm text-gray-500">Access Management Portal</span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="hidden md:block text-sm text-gray-700">
                Remove Roles
              </div>
              <button className="flex items-center text-sm text-gray-500 hover:text-gray-700" onClick={() => navigate('/dashboard')}>
                <LogOut size={16} className="mr-1" />
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <div className={`${sidebarOpen ? 'block' : 'hidden'} md:block md:w-64 bg-white shadow-sm border-r border-gray-200 min-h-screen`}>
          <nav className="mt-8 px-4">
            <div className="space-y-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = tab.id === 'remove';
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabClick(tab.id)}
                    className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                      isActive
                        ? 'bg-primary-50 text-primary-700 border-r-2 border-primary-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <Icon size={20} className={`mr-3 ${tab.color}`} />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-6">
          <div className="max-w-5xl mx-auto">
          {message && (
            <div className={`mb-4 p-4 rounded-lg border ${message.type === 'success' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
              <div className="flex items-center">
                <AlertCircle className={`h-5 w-5 mr-2 ${message.type === 'success' ? 'text-green-500' : 'text-red-500'}`} />
                <div className="flex-1">{message.text}</div>
                <button onClick={() => setMessage(null)} className={`${message.type === 'success' ? 'text-green-700' : 'text-red-700'}`}>
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

            <div className="card mb-8">
              <div className="mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Select a User</h2>
                <p className="text-gray-600">Search for the user whose roles you want to remove</p>
              </div>

              <UserSearch onUserSelect={setSelectedUser} selectedUser={selectedUser} onReset={() => setSelectedUser(null)} />

              {selectedUser && (
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-md font-semibold text-gray-900 flex items-center">
                      <Minus className="h-4 w-4 mr-2 text-red-600" />
                      Current Roles for {selectedUser.name}
                    </h3>
                    <button
                      onClick={handleFetchCurrentRoles}
                      disabled={!selectedUser.sapId || isLoading}
                      className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoading ? 'Loading…' : 'Get Current Roles'}
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label htmlFor="removeRoleSelect" className="block text-sm font-medium text-gray-700 mb-2">
                        Select Role to Remove
                      </label>
                      <select
                        id="removeRoleSelect"
                        value={selectedToRemove.length > 0 ? selectedToRemove[0] : ''}
                        onChange={(e) => {
                          if (e.target.value) {
                            setSelectedToRemove([e.target.value]);
                          } else {
                            setSelectedToRemove([]);
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      >
                        <option value="">-- Select a role to remove --</option>
                        {selectedUser.currentRoles.map((role) => (
                          <option key={role} value={role}>
                            {role}
                          </option>
                        ))}
                      </select>
                    </div>

                    {selectedToRemove.length > 0 && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-red-800">Selected Role to Remove:</p>
                            <p className="text-sm text-red-700">{selectedToRemove[0]}</p>
                          </div>
                          <button onClick={() => setSelectedToRemove([])} className="text-red-600 hover:text-red-800">
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="flex justify-end">
                      <button
                        onClick={submitRemoveRoles}
                        disabled={selectedToRemove.length === 0 || isLoading}
                        className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 disabled:opacity-50 flex items-center text-sm"
                      >
                        <Send className="h-4 w-4 mr-1" />
                        Request Removal
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RemoveRolePage;


